import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input, Button, Table, Dropdown } from "antd";
import type { MenuProps, TableColumnsType } from "antd";
import { useDebounce } from "@react-hook/debounce";
import { ApolloError } from "@apollo/client";
import { HiSearch } from "react-icons/hi";
import dayjs from "dayjs";

import StatusTag from "components/status-tag/status-tag";
import routeNames from "constants/route-names";
import { PropertyWarrantyStatus, UserRoleValue } from "constants/field-values";
import { useGetUserPropertiesFilteredQuery } from "graphql/queries/get-user-properties-filtered.generated";
import { Organizations, Properties } from "graphql/generated/graphql";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useAdminPropertiesFiltersStore } from "state/zustand-stores/admin-properties-filters-store";
import { createLabelForFilters } from "utils/helpers";
import { SEARCH_DEBOUNCE_DELAY_MS } from "constants/constants";

const tableColumns: TableColumnsType = [
  {
    title: "PROPERTY",
    dataIndex: "name",
    key: "PROPERTY",
  },
  {
    title: "ORGANIZATION",
    render: (field, record, index) => {
      if (record.organization && record.organization.name) {
        return `${record.organization.name}`;
      }
      return ``;
    },
    key: `ORGANIZATION`,
  },
  {
    title: "CONTACT",
    render: (field, record, index) => {
      if (record.property_contacts?.[0]?.name) {
        return `${record.property_contacts[0].name}`;
      }

      if (record.organization && record.organization.contact_name) {
        return `${record.organization.contact_name}`;
      }

      if (record.organization && record.organization.email) {
        return `${record.organization.email}`;
      }
      return ``;
    },
    key: `CONTACT`,
  },
  {
    title: "WARRANTY EXPIRATION",
    dataIndex: "warranty_expiration_date",
    render: (item) => {
      const formattedDate = dayjs(item).format("MM/DD/YYYY");
      return formattedDate;
    },
    key: `WARRANTY EXPIRATION`,
  },
  {
    title: "WARRANTY STATUS",
    dataIndex: "warranty_expiration_date",
    render: (item) => {
      const dateNOW = dayjs();
      const expirationDate = dayjs(item);
      const dateDiff = expirationDate.diff(dateNOW, "day");

      if (dateDiff > 90) {
        return <StatusTag status={PropertyWarrantyStatus.Active} />;
      } else if (dateDiff > 0) {
        return <StatusTag status={PropertyWarrantyStatus.ExpiringSoon} />;
      } else {
        return <StatusTag status={PropertyWarrantyStatus.Expired} />;
      }
    },
    key: `WARRANTY STATUS`,
  },
];

const AdminProperties: React.FC = () => {
  const navigate = useNavigate();
  const { loggedUserValue } = useLoggedUser();
  const [dataSource, setDataSource] = useState<Array<Properties>>([]);

  const [organizationsDropdownOpen, setOrganizationsDropdownOpen] =
    useState<boolean>(false);

  const [warrantyStatusDropdownOpen, setyWarrantyStatusDropdownOpen] =
    useState<boolean>(false);

  const selectedOrganizationsFilter = useAdminPropertiesFiltersStore(
    (state) => state.filters.selectedOrganizationsFilter
  );

  const setOrganizationFilters = useAdminPropertiesFiltersStore(
    (state) => state.setOrganizationFilters
  );

  const selectedWarrantyStatusFilter = useAdminPropertiesFiltersStore(
    (state) => state.filters.selectedWarrantyStatusFilter
  );

  const setWarrantyStatusFilters = useAdminPropertiesFiltersStore(
    (state) => state.setWarrantyStatusFilters
  );

  const searchTerm = useAdminPropertiesFiltersStore(
    (state) => state.filters.searchAdminPropertiesTerm
  );

  const setSearchTerm = useAdminPropertiesFiltersStore(
    (state) => state.setSearchTerm
  );

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(
    searchTerm,
    SEARCH_DEBOUNCE_DELAY_MS
  );

  useEffect(() => {
    setDebouncedSearchTerm(searchTerm);
  }, [searchTerm]);

  const organizationOptionsValues = useMemo(() => {
    const { user_properties } = loggedUserValue;

    const organizationsSet: Set<string> = new Set();

    user_properties.forEach((user_prop) => {
      if (user_prop.property) {
        const { organization } = user_prop.property;

        if (organization) {
          const organizationString = JSON.stringify(organization);
          organizationsSet.add(organizationString);
        }
      }
    });

    const organizations: Array<Organizations> = [...organizationsSet].map(
      (element) => JSON.parse(element)
    );

    return organizations;
  }, [loggedUserValue.user_properties]);

  const propertiesFilter = useMemo(() => {
    const whereClause: any = {
      user_id: { _eq: loggedUserValue.id },
      property: { deleted_at: { _is_null: true } },
    };

    if (selectedOrganizationsFilter && selectedOrganizationsFilter.length) {
      whereClause.property.organization_id = {
        _in: selectedOrganizationsFilter,
      };
    } else if (whereClause.property.organization_id) {
      delete whereClause.property.organization_id;
    }

    if (selectedWarrantyStatusFilter && selectedWarrantyStatusFilter.length) {
      const dateNOW = dayjs();
      const date90 = dateNOW.add(90, "day");

      const conditions: any[] = [];

      // Active: if the difference between today and expiration is greater than 90 days
      if (
        selectedWarrantyStatusFilter.includes(PropertyWarrantyStatus.Active)
      ) {
        conditions.push({
          property: {
            warranty_expiration_date: { _gte: date90.toISOString() },
          },
        });
      }

      // Expiring Soon: If the difference between today and expiration is less than 90 days but greater than 0
      if (
        selectedWarrantyStatusFilter.includes(
          PropertyWarrantyStatus.ExpiringSoon
        )
      ) {
        conditions.push({
          _and: [
            {
              property: {
                warranty_expiration_date: { _gte: dateNOW.toISOString() },
              },
            },
            {
              property: {
                warranty_expiration_date: { _lt: date90.toISOString() },
              },
            },
          ],
        });
      }

      // Expired: if the difference between today and expiration is negative
      if (
        selectedWarrantyStatusFilter.includes(PropertyWarrantyStatus.Expired)
      ) {
        conditions.push({
          property: {
            warranty_expiration_date: { _lt: dateNOW.toISOString() },
          },
        });
      }

      if (conditions.length > 0) {
        whereClause._or = conditions;
      }
    }

    if (debouncedSearchTerm) {
      const searchConditions = [
        { property: { name: { _ilike: `%${debouncedSearchTerm}%` } } },
        { property: { address: { _ilike: `%${debouncedSearchTerm}%` } } },
      ];

      if (whereClause._or) {
        whereClause._and = [
          { _or: whereClause._or },
          { _or: searchConditions },
        ];
        delete whereClause._or;
      } else {
        whereClause._or = searchConditions;
      }
    }

    return whereClause;
  }, [
    debouncedSearchTerm,
    loggedUserValue,
    selectedOrganizationsFilter,
    selectedWarrantyStatusFilter,
  ]);

  const handleOnCompletePropertiesFiltered = useCallback((data) => {
    const { user_properties } = data;

    const properties = user_properties.map((user_prop) => user_prop.property);

    setDataSource(properties);
  }, []);

  const handleOnErrorPropertiesFiltered = useCallback((error: ApolloError) => {
    console.log("handleOnErrorPropertiesFiltered Error: ", error);
  }, []);

  const { data, loading, error } = useGetUserPropertiesFilteredQuery({
    variables: {
      where: propertiesFilter,
    },
    errorPolicy: "none",
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorPropertiesFiltered,
    onCompleted: handleOnCompletePropertiesFiltered,
  });

  const handleSearchTermChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Directly update the search term in Zustand
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  const handleOnSelectOrganizationsFilter = useCallback(
    (args) => {
      const { selectedKeys } = args;

      if (selectedKeys.includes("organization-dropdown-option-all")) {
        setOrganizationFilters([]);
        return;
      }

      setOrganizationFilters(selectedKeys);
    },
    [setOrganizationFilters]
  );

  const [organizationOptions, organizationLabel = "Organization"] =
    useMemo(() => {
      if (organizationOptionsValues && organizationOptionsValues.length) {
        let selectedOrganizationName;

        const propertyOrganizations: MenuProps["items"] =
          organizationOptionsValues.reduce(
            (options, organization, currentindex, array) => {
              const optionId = `organization-dropdown-option-${
                currentindex + 1
              }-${organization?.id}`;

              const isSelected = selectedOrganizationsFilter.includes(
                organization?.id as never
              );

              if (!selectedOrganizationName && isSelected) {
                selectedOrganizationName = organization.name;
              }

              const newOption = {
                key: `${organization?.id}`,
                icon: (
                  <div>
                    <input
                      className="custom-checkbox"
                      id={optionId}
                      type="checkbox"
                      onClick={(e) => e.stopPropagation()}
                      checked={isSelected}
                      onChange={() => undefined}
                    />
                    <label htmlFor={optionId} className="custom-checkbox-label">
                      {organization?.name}
                    </label>
                  </div>
                ),
              };

              options.push(newOption);

              return options;
            },
            [
              {
                key: "organization-dropdown-option-all",
                icon: (
                  <div>
                    <input
                      className="custom-checkbox"
                      id="organization-dropdown-option-all"
                      type="checkbox"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <label
                      htmlFor="organization-dropdown-option-all"
                      className="custom-checkbox-label"
                    >
                      All organizations
                    </label>
                  </div>
                ),
              },
            ]
          );

        return [
          propertyOrganizations,
          createLabelForFilters(
            "Organization",
            selectedOrganizationsFilter,
            selectedOrganizationName
          ),
        ];
      }

      return [[], "Organization"];
    }, [organizationOptionsValues, selectedOrganizationsFilter]);

  const handleOnSelectWarrantyStatusFilter = useCallback(
    (args) => {
      const { selectedKeys } = args;

      if (selectedKeys.includes("status-dropdown-option-all")) {
        setWarrantyStatusFilters([]);
        return;
      }

      setWarrantyStatusFilters(selectedKeys);
    },
    [setWarrantyStatusFilters]
  );

  const [warrantyStatusOptions, warrantyStatusLabel] = useMemo(() => {
    const warrantyStatuses = Object.values(PropertyWarrantyStatus) as string[];

    const propertyWarrantyStatuses: MenuProps["items"] =
      warrantyStatuses.reduce(
        (options, status, currentindex, array) => {
          const optionId = `warranty-status-dropdown-option-${status}-${
            currentindex + 1
          }}`;

          const isSelected = selectedWarrantyStatusFilter.includes(
            status as never
          );

          const newOption = {
            key: `${status}`,
            icon: (
              <div>
                <input
                  className="custom-checkbox"
                  id={optionId}
                  type="checkbox"
                  onClick={(e) => e.stopPropagation()}
                  checked={isSelected}
                  onChange={() => undefined}
                />
                <label htmlFor={optionId} className="custom-checkbox-label">
                  {status}
                </label>
              </div>
            ),
          };

          options.push(newOption);

          return options;
        },
        [
          {
            key: "status-dropdown-option-all",
            icon: (
              <div>
                <input
                  className="custom-checkbox"
                  id="status-dropdown-option-all"
                  type="checkbox"
                  onClick={(e) => e.stopPropagation()}
                />
                <label
                  htmlFor="status-dropdown-option-all"
                  className="custom-checkbox-label"
                >
                  All Statuses
                </label>
              </div>
            ),
          },
        ]
      );

    return [
      propertyWarrantyStatuses,
      createLabelForFilters("Warranty Status", selectedWarrantyStatusFilter),
    ];
  }, [selectedWarrantyStatusFilter]);

  const handleOnClickProperty = useCallback((record) => {
    const { id } = record;
    const path = `${routeNames.ADMIN_PROPERTY_DETAILS.replace(
      ":propertyId",
      id
    )}`;

    navigate(path);
  }, []);

  const isLoading = loading;

  return (
    <div className="admin-products-screen-container">
      <span className="page-title">{"Manage Properties"}</span>
      <div className="admin-products-content-container">
        <div className="admin-products-filter-group">
          <div className="search-products-row">
            <div className="products-search-container">
              <Input
                className="admin-properties-search-input-wrapper"
                name="searchProperties"
                placeholder="Search..."
                classNames={{ input: "admin-properties-search-input" }}
                value={searchTerm} // Use Zustand search term
                autoComplete="off"
                onChange={handleSearchTermChange}
                prefix={<HiSearch color={"#67748E"} size={"16px"} />}
                allowClear
              />
            </div>
          </div>

          <div className="products-filters-and-create-guide-row">
            <div className="products-filters-row">
              <Dropdown
                menu={{
                  items: organizationOptions,
                  selectable: true,
                  multiple: true,
                  onSelect: (args) => handleOnSelectOrganizationsFilter(args),
                  onDeselect: (args) => handleOnSelectOrganizationsFilter(args),
                  selectedKeys: selectedOrganizationsFilter,
                }}
                arrow
                className="admin-properties-dropdown"
                overlayClassName="admin-properties-dropdown-overlay"
                trigger={["click"]}
                open={organizationsDropdownOpen}
                onOpenChange={(
                  open: boolean,
                  info: { source: "trigger" | "menu" }
                ) => {
                  if (info.source === "trigger" && open === false) {
                    setOrganizationsDropdownOpen(false);
                  }
                }}
                disabled={organizationOptions.length === 0}
              >
                <Button
                  onClick={() => {
                    setOrganizationsDropdownOpen(
                      (previousValue) => !previousValue
                    );
                  }}
                >
                  {organizationLabel}
                  <img
                    src={
                      process.env.PUBLIC_URL +
                      "/assets/images/icons/black-chevron-down.svg"
                    }
                    title={organizationLabel}
                    className="chevron-button"
                  />
                </Button>
              </Dropdown>
              <Dropdown
                menu={{
                  items: warrantyStatusOptions,
                  selectable: true,
                  multiple: true,
                  onSelect: (args) => handleOnSelectWarrantyStatusFilter(args),
                  onDeselect: (args) =>
                    handleOnSelectWarrantyStatusFilter(args),
                  selectedKeys: selectedWarrantyStatusFilter,
                }}
                arrow
                className="admin-properties-dropdown"
                overlayClassName="admin-properties-dropdown-overlay"
                trigger={["click"]}
                open={warrantyStatusDropdownOpen}
                onOpenChange={(
                  open: boolean,
                  info: { source: "trigger" | "menu" }
                ) => {
                  if (info.source === "trigger" && open === false) {
                    setyWarrantyStatusDropdownOpen(false);
                  }
                }}
                disabled={warrantyStatusOptions.length === 0}
              >
                <Button
                  onClick={() => {
                    setyWarrantyStatusDropdownOpen(
                      (previousValue) => !previousValue
                    );
                  }}
                >
                  {warrantyStatusLabel}
                  <img
                    src={
                      process.env.PUBLIC_URL +
                      "/assets/images/icons/black-chevron-down.svg"
                    }
                    title={warrantyStatusLabel}
                    className="chevron-button"
                  />
                </Button>
              </Dropdown>
            </div>
            <div className="admin-properties-operations">
              <Button
                onClick={() => navigate(routeNames.EAGLE_EYE)}
                style={{ borderRadius: 26, height: 44, fontFamily: "Steradian-Medium", fontSize: 14, padding: '0 24px' }}
              >
                <img src={process.env.PUBLIC_URL + "/assets/images/icons/een-button-image.svg"} />
                Video Search
              </Button>
              {loggedUserValue.role === UserRoleValue.customer_manager ? null : (
                <Link
                  to={routeNames.ADMIN_PROPERTY_ADD}
                  className="highnote-button"
                >
                  Add Property
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="admin-properties-table-container">
          <Table
            dataSource={dataSource}
            columns={tableColumns}
            loading={isLoading}
            rowKey={(record) => record.id}
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  handleOnClickProperty(record);
                },
              };
            }}
            style={{ marginBottom: "25px" }}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminProperties;
